/**
 * Send → the customer gets their report by email, from support@canadianinsulationexperts.com.
 *
 * The email itself (logo, "Your attic today" R-value card, the R-60 benefits, two "View your report"
 * buttons, signature) is built in n8n "[C] App 11 - Report Email" (REACT_APP_REPORT_EMAIL_URL) —
 * the app only says who, which report and which PDFs. The quote PDFs are attached, not mentioned.
 *
 * It runs after the quotes, because the PDFs only exist once quoteSend has built and uploaded them:
 * pushQuotes queues it with their links. Shows in the customer's thread + a Note on the Deal.
 */
import { getAccessToken } from './auth';
import { isEmail } from './communications';
import { queueChange, registerZohoAction } from './sync';

const REPORT_EMAIL_URL = process.env.REACT_APP_REPORT_EMAIL_URL;
export const REPORT_EMAIL_LIVE = !!REPORT_EMAIL_URL;

// "Your attic today": the report's measured R-value (the depth finding carries it). None measured → null.
export const reportRValue = (report) => {
  const s = ((report && report.sections) || []).find((x) => x.metrics && x.metrics.on && x.metrics.curR != null);
  if (!s) return null;
  const m = s.metrics;
  return { cur: Number(m.curR), target: Number(m.tgtR) || 60, depth: parseFloat(m.depth) || null, material: m.material || '' };
};

const noSignal = (message) => Object.assign(new Error(message), { offline: true });

export const pushReportEmail = async (job) => {
  const auth = await getAccessToken();
  if (!auth) throw new Error('Sign in again to email the customer.');
  let res;
  try {
    res = await fetch(REPORT_EMAIL_URL, {
      method: 'POST',
      headers: { Authorization: `Bearer ${auth}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'send', ...job }),
    });
  } catch {
    throw noSignal('No connection — the report email will go out when you’re back online.');
  }
  const out = await res.json().catch(() => ({}));
  if (!res.ok || !out.ok) {
    const err = new Error(out.error || `The report email wasn’t sent (${res.status}).`);
    err.offline = res.status >= 500 && !out.error;
    throw err;
  }
  return out;
};

registerZohoAction('email_report', (zoho) => {
  const { action, ...job } = zoho;
  return pushReportEmail(job);
});

/**
 * Called by pushQuotes once the PDFs are uploaded. email = what Send handed over
 * ({ to, name, token, contact, resend, rvalue }); pdfs = [{ url, fileName }].
 * A newer send for the same inspection replaces an email still waiting.
 */
export const emailReport = ({ inspectionId, email, pdfs = [] }) => {
  if (!REPORT_EMAIL_LIVE || !inspectionId || !email || !email.token || !isEmail(email.to)) return { skipped: true };
  return {
    queued: true,
    changeId: queueChange({
      type: 'report.email',
      leadId: inspectionId,
      label: `${email.name || 'Customer'} — report emailed to ${email.to}`,
      payload: { to: email.to, pdfs: pdfs.length },
      zoho: { action: 'email_report', inspectionId, ...email, to: email.to.trim().toLowerCase(), pdfs },
      supersede: `report-email:${inspectionId}`,
    }),
  };
};
